import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, CheckCircle, ChevronRight, Mail,
  ArrowRight, Lock, Shield, ChevronDown
} from "lucide-react";

/* ─── Data (sin cambios) ─── */
const PLANS = [
  {
    name: "Básico",
    price: "17.000",
    description: "Ideal para empresas que están empezando a contratar con el Estado.",
    features: [
      "Radar SECOP II completo",
      "Análisis IA (50 procesos/día)",
      "Score automático 0-100",
      "Alertas personalizadas",
      "1 usuario por cuenta",
      "Soporte por email",
    ],
    notIncluded: ["IA ilimitada", "Múltiples usuarios", "Soporte prioritario"],
  },
  {
    name: "Premium",
    price: "29.700",
    description: "Para empresas que quieren maximizar sus oportunidades de contratación.",
    badge: "MÁS POPULAR",
    features: [
      "Todo lo del plan Básico",
      "Análisis IA ilimitado",
      "Múltiples usuarios",
      "Perfil de empresa avanzado",
      "Soporte prioritario",
      "Acceso a nuevas funciones",
    ],
    notIncluded: [],
  },
];

const FAQS = [
  { q: "¿Cómo funciona la prueba gratuita?", a: "Al registrarte obtienes 7 días de acceso completo a todas las funciones sin necesidad de tarjeta de crédito. Al finalizar, seleccionas un plan." },
  { q: "¿Puedo cambiar de plan después?", a: "Sí, puedes actualizar o cambiar tu plan en cualquier momento. El cambio aplica en tu próximo ciclo de facturación." },
  { q: "¿Cómo pago?", a: "Actualmente aceptamos pagos por transferencia bancaria o PSE. Escríbenos al email de soporte y te enviamos los datos de pago." },
  { q: "¿Mis datos están seguros?", a: "Sí. Cada empresa tiene un espacio de datos completamente aislado. Tu información nunca se comparte con otros usuarios." },
];

const TABLE_ROWS = [
  ["Radar SECOP II",          "✓",          "✓"],
  ["Análisis IA diario",       "50 procesos", "Ilimitado"],
  ["Score automático 0-100",   "✓",          "✓"],
  ["Alertas personalizadas",   "✓",          "✓"],
  ["Perfil empresarial",       "Básico",      "Avanzado"],
  ["Usuarios por cuenta",      "1",          "Múltiples"],
  ["Favoritos y seguimiento",  "✓",          "✓"],
  ["Sincronización SECOP",     "Diaria",      "Diaria + Manual"],
  ["Soporte",                  "Email",       "Prioritario"],
];

/* ─── Animation variants ─── */
const SV = { hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.08 } } };
const IV = {
  hidden: { opacity: 0, y: 20, filter: "blur(2px)" },
  show:   { opacity: 1, y: 0,  filter: "blur(0px)", transition: { duration: 0.5, ease: "easeOut" } },
};
const VP = { once: true, margin: "-70px" };

/* ─── FAQ Accordion (mismo estilo que Landing) ─── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl overflow-hidden mb-3"
      style={{
        background: "white",
        border: "1px solid #E2E8F0",
        boxShadow: open ? "0 4px 24px rgba(16,185,129,0.08)" : "0 1px 4px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.3s ease",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left transition-colors"
        style={{ background: open ? "#F8FAFC" : "white" }}
      >
        <span className="font-semibold text-slate-800 text-sm sm:text-base pr-4 leading-snug">{q}</span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="shrink-0"
        >
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-6 py-4 border-t border-slate-100" style={{ background: "#F8FAFC" }}>
              <p className="text-slate-600 text-sm leading-relaxed">{a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function PricingPage() {
  const subject = encodeURIComponent("Quiero activar mi plan en RadarLit");
  const body    = encodeURIComponent("Hola, estoy interesado en activar el plan [Básico/Premium] en RadarLit. Mi email de registro es: ");
  const mailtoLink = `mailto:soporteradarlit@gmail.com?subject=${subject}&body=${body}`;

  return (
    <div className="min-h-screen text-slate-100 overflow-x-hidden">

      {/* ════ NAVBAR (idéntico a LandingPage) ════ */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{
          background: "rgba(2,6,23,0.90)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 1px 40px rgba(0,0,0,0.5)",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: "rgba(16,185,129,0.12)",
                border: "1px solid rgba(16,185,129,0.35)",
                boxShadow: "0 0 16px rgba(16,185,129,0.2)",
              }}
            >
              <Target className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-bold text-white tracking-tight">RadarLit</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-slate-400 hover:text-slate-200 text-sm transition-colors duration-200 px-3 py-1.5">
              Iniciar sesión
            </Link>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Link
                to="/register"
                className="flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl"
                style={{
                  background: "linear-gradient(135deg, #10B981, #06B6D4)",
                  color: "#020617",
                  boxShadow: "0 0 20px rgba(16,185,129,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
                }}
              >
                Comenzar gratis <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* ════ HERO — oscuro con aurora ════ */}
      <section
        className="relative overflow-hidden"
        style={{ background: "#020617", paddingBottom: "6rem" }}
      >
        {/* Noise */}
        <div className="pointer-events-none absolute inset-0 noise-overlay z-0" aria-hidden />
        {/* Auroras */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
          <div className="aurora-1 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 70% 60% at 20% 30%, rgba(16,185,129,0.18) 0%, transparent 100%)" }} />
          <div className="aurora-2 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 60% 50% at 80% 25%, rgba(6,182,212,0.14) 0%, transparent 100%)" }} />
          <div className="aurora-3 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 45% 40% at 50% 90%, rgba(139,92,246,0.11) 0%, transparent 100%)" }} />
        </div>
        {/* Grid */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: "linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            opacity: 0.025,
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black 20%, transparent 75%)",
          }}
        />

        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } }}
          className="relative z-10 max-w-4xl mx-auto px-5 pt-16 pb-4 text-center"
        >
          {/* Badge */}
          <motion.div
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6"
            style={{
              background: "rgba(16,185,129,0.09)",
              border: "1px solid rgba(16,185,129,0.24)",
              boxShadow: "0 0 20px rgba(16,185,129,0.08)",
            }}
          >
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-emerald-400 text-xs font-semibold">7 días gratis · Sin tarjeta</span>
          </motion.div>

          {/* H1 */}
          <motion.h1
            variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.65 } } }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-4"
          >
            <span className="text-white">Planes simples</span><br />
            <span style={{
              background: "linear-gradient(90deg, #34D399, #22D3EE, #A78BFA)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
              y transparentes
            </span>
          </motion.h1>

          <motion.p
            variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.55 } } }}
            className="text-base sm:text-lg max-w-xl mx-auto"
            style={{ color: "#94A3B8" }}
          >
            Elige el plan que mejor se adapte a tu empresa. Comienza gratis y activa cuando estés listo.
          </motion.p>
        </motion.div>
      </section>

      {/* ════ PLANS — fondo blanco ════ */}
      <section className="relative z-10 bg-white py-16 lg:py-20"
        style={{ borderBottom: "1px solid #F1F5F9" }}
      >
        <div className="max-w-4xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14 } } }}
            className="grid sm:grid-cols-2 gap-6 items-start"
          >
            {/* Plan Básico */}
            <motion.div
              variants={IV}
              className="bg-white rounded-2xl p-8 flex flex-col"
              style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
              whileHover={{ y: -2, boxShadow: "0 10px 32px rgba(0,0,0,0.08)" }}
              transition={{ duration: 0.22 }}
            >
              <div className="mb-6">
                <p className="text-sm font-medium mb-1" style={{ color: "#64748B" }}>Plan Básico</p>
                <div className="flex items-end gap-1 mb-2">
                  <span className="font-black font-mono text-4xl" style={{ color: "#0F172A" }}>$17.000</span>
                  <span className="text-sm mb-1.5" style={{ color: "#94A3B8" }}>COP/mes</span>
                </div>
                <p className="text-xs" style={{ color: "#94A3B8" }}>Ideal para empresas que están empezando a contratar con el Estado.</p>
              </div>
              <ul className="space-y-2.5 flex-1 mb-6">
                {PLANS[0].features.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "#475569" }}>
                    <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#10B981" }} />{f}
                  </li>
                ))}
                {PLANS[0].notIncluded.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm line-through" style={{ color: "#CBD5E1" }}>
                    <span className="w-4 h-4 shrink-0 mt-0.5 text-center text-xs">✕</span>{f}
                  </li>
                ))}
              </ul>
              <div className="space-y-2">
                <Link
                  to="/register"
                  className="block w-full text-center rounded-xl py-3 text-sm font-semibold"
                  style={{ border: "1px solid #CBD5E1", color: "#475569", transition: "border-color 0.2s, color 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#10B981"; e.currentTarget.style.color = "#059669"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.color = "#475569"; }}
                >
                  Empezar gratis 7 días
                </Link>
                <a
                  href={mailtoLink}
                  className="flex items-center justify-center gap-1.5 w-full text-center text-xs py-1.5 transition-colors"
                  style={{ color: "#94A3B8" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#64748B"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#94A3B8"; }}
                >
                  <Mail className="w-3.5 h-3.5" /> Activar plan por email
                </a>
              </div>
            </motion.div>

            {/* Plan Premium */}
            <motion.div
              variants={IV}
              className="relative rounded-2xl p-8 flex flex-col"
              style={{
                background: "linear-gradient(160deg, rgba(236,253,245,0.65) 0%, #ffffff 38%)",
                border: "2px solid #10B981",
                boxShadow: "0 0 0 4px rgba(16,185,129,0.06), 0 24px 64px rgba(16,185,129,0.13), 0 4px 20px rgba(0,0,0,0.05)",
                transform: "scale(1.02)",
              }}
              whileHover={{ y: -2, boxShadow: "0 0 0 4px rgba(16,185,129,0.08), 0 30px 80px rgba(16,185,129,0.18), 0 4px 20px rgba(0,0,0,0.06)" }}
              transition={{ duration: 0.22 }}
            >
              <div
                className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-white text-[11px] font-black tracking-wider px-4 py-1 rounded-full shadow-md"
                style={{ background: "linear-gradient(135deg, #059669, #0891B2)" }}
              >
                MÁS POPULAR
              </div>
              <div className="mb-6">
                <p className="text-sm font-bold mb-1" style={{ color: "#059669" }}>Plan Premium</p>
                <div className="flex items-end gap-1 mb-2">
                  <span className="font-black font-mono text-4xl" style={{ color: "#0F172A" }}>$29.700</span>
                  <span className="text-sm mb-1.5" style={{ color: "#94A3B8" }}>COP/mes</span>
                </div>
                <p className="text-xs" style={{ color: "#94A3B8" }}>Para empresas que quieren maximizar sus oportunidades de contratación.</p>
              </div>
              <ul className="space-y-2.5 flex-1 mb-6">
                {PLANS[1].features.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "#475569" }}>
                    <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#10B981" }} />{f}
                  </li>
                ))}
              </ul>
              <div className="space-y-2">
                <Link
                  to="/register"
                  className="block w-full text-center font-extrabold rounded-xl py-3 text-sm"
                  style={{
                    background: "linear-gradient(135deg, #10B981, #06B6D4)",
                    color: "#020617",
                    boxShadow: "0 0 24px rgba(16,185,129,0.4)",
                    transition: "box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 38px rgba(16,185,129,0.65)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 24px rgba(16,185,129,0.4)"; }}
                >
                  Empezar gratis 7 días
                </Link>
                <a
                  href={mailtoLink}
                  className="flex items-center justify-center gap-1.5 w-full text-center text-xs py-1.5 transition-colors"
                  style={{ color: "#94A3B8" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#64748B"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "#94A3B8"; }}
                >
                  <Mail className="w-3.5 h-3.5" /> Activar plan por email
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Trust note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={VP}
            className="text-center text-xs mt-6 flex items-center justify-center gap-1.5"
            style={{ color: "#94A3B8" }}
          >
            <Lock className="w-3 h-3" /> Pago seguro · Sin compromiso · Cancela cuando quieras
          </motion.p>
        </div>
      </section>

      {/* ════ TABLA COMPARATIVA — gris claro ════ */}
      <section
        className="relative z-10 py-16 lg:py-20"
        style={{ background: "#F1F5F9", borderBottom: "1px solid #E2E8F0" }}
      >
        <div className="max-w-4xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
          >
            <motion.h2
              variants={IV}
              className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center mb-10"
              style={{ color: "#0F172A" }}
            >
              Comparación detallada
            </motion.h2>

            <motion.div
              variants={IV}
              className="rounded-2xl overflow-hidden"
              style={{
                background: "white",
                border: "1px solid #E2E8F0",
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
              }}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: "1px solid #F1F5F9", background: "#F8FAFC" }}>
                      <th className="text-left px-6 py-4 text-xs font-bold tracking-wider uppercase w-1/2" style={{ color: "#64748B" }}>Función</th>
                      <th className="text-center px-6 py-4 text-xs font-bold tracking-wider uppercase" style={{ color: "#64748B" }}>Básico</th>
                      <th className="text-center px-6 py-4 text-xs font-bold tracking-wider uppercase" style={{ color: "#059669" }}>Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TABLE_ROWS.map(([fn, basic, premium], i) => (
                      <tr
                        key={fn}
                        style={{
                          borderBottom: i < TABLE_ROWS.length - 1 ? "1px solid #F8FAFC" : "none",
                          background: i % 2 === 0 ? "white" : "#FAFAFA",
                        }}
                      >
                        <td className="px-6 py-3.5 font-medium" style={{ color: "#374151" }}>{fn}</td>
                        <td className="px-6 py-3.5 text-center text-xs" style={{ color: "#94A3B8" }}>{basic}</td>
                        <td className="px-6 py-3.5 text-center text-xs font-semibold" style={{ color: "#059669" }}>{premium}</td>
                      </tr>
                    ))}
                    <tr style={{ background: "#F0FDF4", borderTop: "2px solid #A7F3D0" }}>
                      <td className="px-6 py-4 font-bold" style={{ color: "#0F172A" }}>Precio/mes</td>
                      <td className="px-6 py-4 text-center font-bold" style={{ color: "#374151" }}>$17.000 COP</td>
                      <td className="px-6 py-4 text-center font-bold" style={{ color: "#059669" }}>$29.700 COP</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ════ FAQ — blanco ════ */}
      <section
        className="relative z-10 py-16 lg:py-20 bg-white"
        style={{ borderBottom: "1px solid #F1F5F9" }}
      >
        <div className="max-w-2xl mx-auto px-5">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="text-center mb-10"
          >
            <motion.h2 variants={IV} className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2" style={{ color: "#0F172A" }}>
              Preguntas frecuentes
            </motion.h2>
            <motion.p variants={IV} className="text-sm" style={{ color: "#64748B" }}>
              Todo lo que necesitas saber antes de elegir tu plan.
            </motion.p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
          >
            {FAQS.map(faq => (
              <motion.div key={faq.q} variants={IV}>
                <FaqItem {...faq} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════ CTA FINAL — oscuro con aurora ════ */}
      <section
        className="relative z-10 overflow-hidden"
        style={{ background: "#020617" }}
      >
        {/* Auroras */}
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <div className="aurora-1 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 60% 50% at 18% 50%, rgba(16,185,129,0.17) 0%, transparent 100%)" }} />
          <div className="aurora-2 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 55% 45% at 82% 50%, rgba(6,182,212,0.13) 0%, transparent 100%)" }} />
          <div className="aurora-3 absolute inset-0"
            style={{ background: "radial-gradient(ellipse 42% 42% at 52% 82%, rgba(139,92,246,0.10) 0%, transparent 100%)" }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-5 py-20 lg:py-24">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={VP}
            variants={SV}
            className="relative rounded-3xl p-10 sm:p-14 text-center overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(6,11,24,0.97) 50%, rgba(139,92,246,0.06) 100%)",
              border: "1px solid rgba(16,185,129,0.18)",
              boxShadow: "0 0 0 1px rgba(16,185,129,0.06), 0 0 100px rgba(16,185,129,0.08)",
            }}
          >
            {/* Glow interno */}
            <div
              className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-80 h-36"
              style={{ background: "radial-gradient(ellipse at center, rgba(16,185,129,0.11) 0%, transparent 70%)", filter: "blur(24px)" }}
            />

            <motion.h2 variants={IV}
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-3"
            >
              ¿Listo para empezar?
            </motion.h2>

            <motion.p variants={IV} className="mb-8 max-w-md mx-auto text-sm sm:text-base" style={{ color: "#64748B" }}>
              7 días gratis, sin tarjeta de crédito. Cancela cuando quieras.
            </motion.p>

            <motion.div variants={IV}>
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2.5 font-extrabold px-10 py-4 rounded-xl text-base group"
                  style={{
                    background: "linear-gradient(135deg, #10B981, #06B6D4)",
                    color: "#020617",
                    boxShadow: "0 0 50px rgba(16,185,129,0.50)",
                    transition: "box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 0 70px rgba(16,185,129,0.70)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 0 50px rgba(16,185,129,0.50)"; }}
                >
                  Comenzar prueba gratuita
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

            <motion.p variants={IV} className="text-xs mt-4" style={{ color: "#334155" }}>
              Sin tarjeta de crédito · Cancela cuando quieras
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ════ FOOTER ════ */}
      <footer
        className="relative z-10 px-5 py-8 text-center"
        style={{ background: "#020617", borderTop: "1px solid rgba(255,255,255,0.05)" }}
      >
        <div className="flex items-center justify-center gap-1.5">
          <Shield className="w-3 h-3" style={{ color: "rgba(16,185,129,0.4)" }} />
          <p className="text-xs" style={{ color: "#334155" }}>
            © 2025 RadarLit · by Innova App Solutions ·{" "}
            <a
              href="mailto:soporteradarlit@gmail.com"
              className="transition-colors duration-200"
              style={{ color: "#475569" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#94A3B8"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#475569"; }}
            >
              soporteradarlit@gmail.com
            </a>
          </p>
        </div>
      </footer>

    </div>
  );
}
